import { employees } from '@/data';
import User from '@/models/User';
import Vue from 'vue';
import Vuex from 'vuex';
import { EventBus } from './event-bus';
import { collections, db, FieldValue } from './firebaseConfig';
import Employee from './models/Employee';
import Employer from './models/Employer';
import Skill from './models/Skill';
Vue.use(Vuex);

const moduleA = {
  state: {},
  mutations: {},
  actions: {},
  getters: {}
};

const moduleB = {
  state: {},
  mutations: {},
  actions: {}
};

export default new Vuex.Store({
  state: {
    user: new User(),
    employees
  },
  getters: {
    currentUser(state) {
      return state.user;
    },
    employees(state) {
      return state.employees;
    }
  },
  mutations: {
    setUser(state, payload: User) {
      state.user = payload;
    },
    resetUser(state, payload) {
      state.user = undefined;
    },
    updateSkills(state, skills: Skill[]) {
      state.user.employee.skills = skills;
    },
    updateAvailableTimeSlots(state, { date, updatedDayTimeSlots }) {
      state.user.employee.availableTimeSlots[date] = updatedDayTimeSlots;
    },
    populateAvailableTimeSlots(state, availableTimeSlots) {
      state.user.employee.availableTimeSlots = availableTimeSlots;
    },
    deleteAvailableTimeSlots(state, { date, updatedTimeSlots }) {
      state.user.employee.availableTimeSlots[date].slots = updatedTimeSlots;
    }
  },
  actions: {
    getUser: ({ commit }, payload) => {
      db.collection('users')
        .get(payload.email)
        .then(res => {
          commit('getUser', res);
        });
    },
    setUser: (context, payload) => {
      return setUser(context, payload);
    },
    registerEmployee: (context, payload: Employee) => {
      const batch = db.batch();
      const employeeRef = collections.employeeCollection.doc(payload.userEmail);
      const userRef = collections.usersCollection.doc(payload.userEmail);
      batch.set(employeeRef, Object.assign({}, payload));
      batch.update(userRef, { isEmployee: true });
      return batch.commit();
    },
    registerEmployer: (context, payload: Employee) => {
      const batch = db.batch();
      const employerRef = collections.employerCollection.doc(payload.userEmail);
      const userRef = collections.usersCollection.doc(payload.userEmail);
      batch.set(employerRef, Object.assign({}, payload));
      batch.update(userRef, { isEmployer: true });
      return batch.commit();
    },
    addSkill: ({ commit, state }, skill: Skill) => {
      const skills = [...state.user.employee.skills, Object.assign({}, skill)];
      const employeeRef = collections.employeeCollection.doc(state.user.email);
      employeeRef.update({ skills }).then(v => {
        commit('updateSkills', skills);
      });
    },
    editSkill: ({ commit, state }, payload) => {
      const oldSkillIndex = state.user.employee.skills.findIndex(s => s.name === payload.oldSkill.name);
      const skills = [...state.user.employee.skills];
      skills.splice(oldSkillIndex, 1, payload.newSkill);
      const employeeRef = collections.employeeCollection.doc(state.user.email);
      employeeRef.update({ skills }).then(_ => {
        commit('updateSkills', skills);
      });
    },
    deleteSkill: ({ commit, state }, skill: Skill) => {
      const skills = state.user.employee.skills.filter(s => s.name !== skill.name);
      const employeeRef = collections.employeeCollection.doc(state.user.email);
      employeeRef.update({ skills }).then(_ => {
        commit('updateSkills', skills);
      });
    },
    deleteAccount: ({ commit }, payload) => {
      const batch = db.batch();
      const employerRef = collections.employerCollection.doc(payload.email);
      const employeeRef = collections.employerCollection.doc(payload.email);
      const userRef = collections.usersCollection.doc(payload.email);
      batch.delete(employerRef);
      batch.delete(employeeRef);
      batch.delete(userRef);
      return batch.commit().then(_ => {
        commit('resetUser');
      });
    },
    addAvailableTimeSlot: ({ commit, state }, { date, slots }) => {
      const employeeAvailableTimeSlotsRef = db.collection(`employee/${state.user.email}/availableTimeSlots`).doc(date);
      const updatedDayTimeSlots = { date, slots };
      return employeeAvailableTimeSlotsRef.set(updatedDayTimeSlots, { merge: true }).then(_ => {
        commit('updateAvailableTimeSlots', { date, updatedDayTimeSlots });
        return updatedDayTimeSlots;
      });
    },
    deleteAvailableTimeSlot: ({ commit, state }, { date, slot }) => {
      const employeeAvailableTimeSlotsRef = db.collection(`employee/${state.user.email}/availableTimeSlots`).doc(date);
      const currentTimeSlots = state.user.employee.availableTimeSlots[date].slots;
      const toDeleteTimeSlotIndex = currentTimeSlots.findIndex(
        s => s.startTime === slot.startTime && s.endTime === slot.endTime
      );
      const updatedTimeSlots = [
        ...currentTimeSlots.slice(0, toDeleteTimeSlotIndex),
        ...currentTimeSlots.slice(toDeleteTimeSlotIndex + 1)
      ];
      employeeAvailableTimeSlotsRef.set({ date, slots: updatedTimeSlots }, { merge: true }).then(_ => {
        commit('deleteAvailableTimeSlots', { date, updatedTimeSlots });
      });
    },
    getAvailableTimeSlot: ({ state, commit }, payload) => {
      return (
        db
          .collection(`employee/${state.user.email}/availableTimeSlots`)
          // .where('date', '==', '2018-11-13')
          .get()
          .then(snapshot => {
            const allDateSlots = {};
            snapshot.forEach(doc => {
              const d = doc.data();
              allDateSlots[d.date] = d;
            });
            commit('populateAvailableTimeSlots', allDateSlots);
            return allDateSlots;
          })
      );
    }
  },
  modules: {
    a: moduleA,
    b: moduleB
  }
});

async function setUser({ commit }, payload) {
  EventBus.$emit('loading', 'loading');
  const user: User = new User(
    payload.email,
    payload.displayName,
    payload.photoURL,
    FieldValue.serverTimestamp(),
    FieldValue.serverTimestamp()
  );
  const doc = await collections.usersCollection.doc(payload.email).get();
  const userRef = collections.usersCollection.doc(payload.email);
  if (doc.exists) {
    if (doc.data().isEmployee) {
      const employee = await collections.employeeCollection.doc(payload.email).get();
      const employeeObject = Object.create(Employee.prototype);
      Object.assign(employeeObject, employee.data());
      user.employee = employeeObject;
      user.isEmployee = true;
    }

    if (doc.data().isEmployer) {
      const employer = await collections.employerCollection.doc(payload.email).get();
      const employerObject = Object.create(Employer.prototype);
      Object.assign(employerObject, employer.data());
      user.employer = employerObject;
      user.isEmployer = true;
    }
    await userRef.update({ lastSignedInAt: user.lastSignedInAt });
    commit('setUser', user);
    EventBus.$emit('loaded');
  } else {
    await userRef.set(Object.assign({}, user));
    commit('setUser', user);
    EventBus.$emit('loaded');
  }
  return user;
}
